import { jsPDF } from "jspdf";
import * as XLSX from "xlsx";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";

interface DailySales {
  date: string;
  total: number;
  count: number;
}

interface PaymentMethodSales {
  name: string;
  total: number;
  count: number;
}

interface TopProduct {
  name: string;
  quantity: number;
  total: number;
}

interface CategorySales {
  name: string;
  total: number;
  count: number;
}

interface ReportsData {
  dailySales: DailySales[];
  paymentMethodSales: PaymentMethodSales[];
  topProducts: TopProduct[];
  categorySales: CategorySales[];
  totalSales: number;
  totalOrders: number;
  averageTicket: number;
}

interface ExportParams {
  data: ReportsData;
  dateFrom: Date;
  dateTo: Date;
  restaurantName?: string;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value);
};

export function exportToPDF({ data, dateFrom, dateTo, restaurantName }: ExportParams) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  let yPos = 20;
  const lineHeight = 7;
  const margin = 14;

  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Relatório de Vendas", pageWidth / 2, yPos, { align: "center" });
  yPos += lineHeight * 1.5;

  if (restaurantName) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(restaurantName, pageWidth / 2, yPos, { align: "center" });
    yPos += lineHeight;
  }

  doc.setFontSize(10);
  doc.text(
    `Período: ${format(dateFrom, "dd/MM/yyyy", { locale: ptBR })} a ${format(dateTo, "dd/MM/yyyy", { locale: ptBR })}`,
    pageWidth / 2,
    yPos,
    { align: "center" }
  );
  yPos += lineHeight * 2;

  // Summary Section
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Resumo", margin, yPos);
  yPos += lineHeight;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Total de Vendas: ${formatCurrency(data.totalSales)}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Total de Pedidos: ${data.totalOrders}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Ticket Médio: ${formatCurrency(data.averageTicket)}`, margin, yPos);
  yPos += lineHeight * 2;

  // Top Products Section
  if (data.topProducts.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Top 10 Produtos", margin, yPos);
    yPos += lineHeight;

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Produto", margin, yPos);
    doc.text("Qtd", 100, yPos);
    doc.text("Total", 130, yPos);
    yPos += lineHeight;

    doc.setFont("helvetica", "normal");
    data.topProducts.slice(0, 10).forEach((product) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(product.name.substring(0, 40), margin, yPos);
      doc.text(String(product.quantity), 100, yPos);
      doc.text(formatCurrency(product.total), 130, yPos);
      yPos += lineHeight;
    });
    yPos += lineHeight;
  }

  // Payment Methods Section
  if (data.paymentMethodSales.length > 0) {
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Formas de Pagamento", margin, yPos);
    yPos += lineHeight;

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Método", margin, yPos);
    doc.text("Vendas", 100, yPos);
    doc.text("Total", 130, yPos);
    yPos += lineHeight;

    doc.setFont("helvetica", "normal");
    data.paymentMethodSales.forEach((method) => {
      doc.text(method.name, margin, yPos);
      doc.text(String(method.count), 100, yPos);
      doc.text(formatCurrency(method.total), 130, yPos);
      yPos += lineHeight;
    });
    yPos += lineHeight;
  }

  // Category Sales Section
  if (data.categorySales.length > 0) {
    if (yPos > 240) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Vendas por Categoria", margin, yPos);
    yPos += lineHeight;

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Categoria", margin, yPos);
    doc.text("Itens", 100, yPos);
    doc.text("Total", 130, yPos);
    yPos += lineHeight;

    doc.setFont("helvetica", "normal");
    data.categorySales.forEach((category) => {
      doc.text(category.name, margin, yPos);
      doc.text(String(category.count), 100, yPos);
      doc.text(formatCurrency(category.total), 130, yPos);
      yPos += lineHeight;
    });
  }

  // Footer
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.text(
      `Gerado em ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })} - Página ${i} de ${totalPages}`,
      pageWidth / 2,
      290,
      { align: "center" }
    );
  }

  // Save
  const fileName = `relatorio-vendas-${format(dateFrom, "yyyy-MM-dd")}-a-${format(dateTo, "yyyy-MM-dd")}.pdf`;
  doc.save(fileName);
}

export function exportToExcel({ data, dateFrom, dateTo, restaurantName }: ExportParams) {
  const workbook = XLSX.utils.book_new();

  // Summary Sheet
  const summaryData = [
    ["Relatório de Vendas"],
    [restaurantName || ""],
    [`Período: ${format(dateFrom, "dd/MM/yyyy")} a ${format(dateTo, "dd/MM/yyyy")}`],
    [],
    ["Resumo"],
    ["Total de Vendas", data.totalSales],
    ["Total de Pedidos", data.totalOrders],
    ["Ticket Médio", data.averageTicket],
  ];
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  XLSX.utils.book_append_sheet(workbook, summarySheet, "Resumo");

  // Daily Sales Sheet
  if (data.dailySales.length > 0) {
    const dailyData = [
      ["Data", "Total", "Pedidos"],
      ...data.dailySales.map((d) => [d.date, d.total, d.count]),
    ];
    const dailySheet = XLSX.utils.aoa_to_sheet(dailyData);
    XLSX.utils.book_append_sheet(workbook, dailySheet, "Vendas Diárias");
  }

  // Top Products Sheet
  if (data.topProducts.length > 0) {
    const productsData = [
      ["Produto", "Quantidade", "Total"],
      ...data.topProducts.map((p) => [p.name, p.quantity, p.total]),
    ];
    const productsSheet = XLSX.utils.aoa_to_sheet(productsData);
    XLSX.utils.book_append_sheet(workbook, productsSheet, "Top Produtos");
  }

  // Payment Methods Sheet
  if (data.paymentMethodSales.length > 0) {
    const paymentData = [
      ["Forma de Pagamento", "Quantidade", "Total"],
      ...data.paymentMethodSales.map((p) => [p.name, p.count, p.total]),
    ];
    const paymentSheet = XLSX.utils.aoa_to_sheet(paymentData);
    XLSX.utils.book_append_sheet(workbook, paymentSheet, "Formas de Pagamento");
  }

  // Category Sales Sheet
  if (data.categorySales.length > 0) {
    const categoryData = [
      ["Categoria", "Itens Vendidos", "Total"],
      ...data.categorySales.map((c) => [c.name, c.count, c.total]),
    ];
    const categorySheet = XLSX.utils.aoa_to_sheet(categoryData);
    XLSX.utils.book_append_sheet(workbook, categorySheet, "Categorias");
  }

  // Save
  const fileName = `relatorio-vendas-${format(dateFrom, "yyyy-MM-dd")}-a-${format(dateTo, "yyyy-MM-dd")}.xlsx`;
  XLSX.writeFile(workbook, fileName);
}

// Generic export functions for any report data
export function exportReportToPDF(data: Record<string, any>[], title: string, dateRange: string) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  
  let yPos = 20;
  const lineHeight = 7;
  const margin = 14;

  // Header
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  doc.text(title, pageWidth / 2, yPos, { align: "center" });
  yPos += lineHeight * 1.5;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Período: ${dateRange}`, pageWidth / 2, yPos, { align: "center" });
  yPos += lineHeight * 2;

  if (data.length === 0) {
    doc.text("Nenhum dado encontrado", pageWidth / 2, yPos, { align: "center" });
  } else {
    // Table headers
    const columns = Object.keys(data[0]);
    const colWidth = (pageWidth - margin * 2) / columns.length;
    
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    columns.forEach((col, i) => {
      doc.text(col, margin + i * colWidth, yPos);
    });
    yPos += lineHeight;

    // Table data
    doc.setFont("helvetica", "normal");
    data.forEach((row) => {
      if (yPos > 270) {
        doc.addPage();
        yPos = 20;
      }
      columns.forEach((col, i) => {
        const value = String(row[col] ?? "");
        doc.text(value.substring(0, 20), margin + i * colWidth, yPos);
      });
      yPos += lineHeight;
    });
  }

  // Footer
  doc.setFontSize(8);
  doc.text(
    `Gerado em ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}`,
    pageWidth / 2,
    290,
    { align: "center" }
  );

  doc.save(`${title.toLowerCase().replace(/ /g, "-")}_${dateRange}.pdf`);
}

export function exportReportToExcel(data: Record<string, any>[], sheetName: string, fileName: string) {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(data);
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, `${fileName}.xlsx`);
}

// ============ CASH REGISTER REPORT EXPORTS ============

interface CashRegisterReportData {
  cashRegister: {
    openedAt: string;
    closedAt: string | null;
    openedBy: string | null;
    closedBy: string | null;
    openingAmount: number;
    notes: string | null;
  };
  cashCount: Array<{ denomination: number; quantity: number; total: number }>;
  salesByPaymentMethod: Array<{ paymentMethodName: string; totalSales: number }>;
  paymentMethodCounts: Array<{ paymentMethodName: string; countedAmount: number }>;
  totals: {
    totalCashCounted: number;
    totalCashSales: number;
    totalPaymentMethodsCounted: number;
    totalSales: number;
    supplies: number;
    withdrawals: number;
  };
}

export function exportCashRegisterReportToPDF(data: CashRegisterReportData, restaurantName?: string) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;
  const lineHeight = 7;
  const margin = 14;

  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Relatório de Fechamento de Caixa", pageWidth / 2, yPos, { align: "center" });
  yPos += lineHeight * 1.5;

  if (restaurantName) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(restaurantName, pageWidth / 2, yPos, { align: "center" });
    yPos += lineHeight;
  }

  // Period info
  doc.setFontSize(10);
  doc.text(`Abertura: ${format(new Date(data.cashRegister.openedAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}`, margin, yPos);
  yPos += lineHeight;
  if (data.cashRegister.closedAt) {
    doc.text(`Fechamento: ${format(new Date(data.cashRegister.closedAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}`, margin, yPos);
    yPos += lineHeight;
  }
  yPos += lineHeight;

  // Summary
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Resumo", margin, yPos);
  yPos += lineHeight;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Fundo Inicial: ${formatCurrency(data.cashRegister.openingAmount)}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Suprimentos: ${formatCurrency(data.totals.supplies)}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Sangrias: ${formatCurrency(data.totals.withdrawals)}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Total de Vendas: ${formatCurrency(data.totals.totalSales)}`, margin, yPos);
  yPos += lineHeight * 2;

  // Cash Count
  if (data.cashCount.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Contagem de Dinheiro", margin, yPos);
    yPos += lineHeight;

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Cédula/Moeda", margin, yPos);
    doc.text("Qtd", 80, yPos);
    doc.text("Total", 100, yPos);
    yPos += lineHeight;

    doc.setFont("helvetica", "normal");
    data.cashCount.forEach((item) => {
      doc.text(formatCurrency(item.denomination), margin, yPos);
      doc.text(String(item.quantity), 80, yPos);
      doc.text(formatCurrency(item.total), 100, yPos);
      yPos += lineHeight;
    });
    doc.setFont("helvetica", "bold");
    doc.text(`Total: ${formatCurrency(data.totals.totalCashCounted)}`, margin, yPos);
    yPos += lineHeight * 2;
  }

  // Payment Methods Comparison
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Comparativo por Forma de Pagamento", margin, yPos);
  yPos += lineHeight;

  doc.setFontSize(9);
  doc.setFont("helvetica", "bold");
  doc.text("Forma de Pagamento", margin, yPos);
  doc.text("Vendido", 100, yPos);
  doc.text("Contado", 140, yPos);
  yPos += lineHeight;

  doc.setFont("helvetica", "normal");
  data.salesByPaymentMethod.forEach((pm) => {
    const counted = data.paymentMethodCounts.find(c => c.paymentMethodName === pm.paymentMethodName);
    doc.text(pm.paymentMethodName, margin, yPos);
    doc.text(formatCurrency(pm.totalSales), 100, yPos);
    doc.text(counted ? formatCurrency(counted.countedAmount) : "-", 140, yPos);
    yPos += lineHeight;
  });

  // Footer
  doc.setFontSize(8);
  doc.text(
    `Gerado em ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}`,
    pageWidth / 2,
    290,
    { align: "center" }
  );

  const fileName = `fechamento-caixa-${format(new Date(data.cashRegister.openedAt), "yyyy-MM-dd")}.pdf`;
  doc.save(fileName);
}

export function exportCashRegisterReportToExcel(data: CashRegisterReportData, restaurantName?: string) {
  const workbook = XLSX.utils.book_new();

  // Summary sheet
  const summaryData = [
    ["Relatório de Fechamento de Caixa"],
    [restaurantName || ""],
    [],
    ["Abertura", format(new Date(data.cashRegister.openedAt), "dd/MM/yyyy HH:mm")],
    ["Fechamento", data.cashRegister.closedAt ? format(new Date(data.cashRegister.closedAt), "dd/MM/yyyy HH:mm") : "Em aberto"],
    [],
    ["Resumo"],
    ["Fundo Inicial", data.cashRegister.openingAmount],
    ["Suprimentos", data.totals.supplies],
    ["Sangrias", data.totals.withdrawals],
    ["Total de Vendas", data.totals.totalSales],
    ["Total Dinheiro Contado", data.totals.totalCashCounted],
    ["Total Outros Métodos Contado", data.totals.totalPaymentMethodsCounted],
  ];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryData), "Resumo");

  // Cash count sheet
  if (data.cashCount.length > 0) {
    const cashData = [
      ["Cédula/Moeda", "Quantidade", "Total"],
      ...data.cashCount.map(c => [c.denomination, c.quantity, c.total])
    ];
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(cashData), "Contagem Dinheiro");
  }

  // Payment methods sheet
  const pmData = [
    ["Forma de Pagamento", "Vendido", "Contado", "Diferença"],
    ...data.salesByPaymentMethod.map(pm => {
      const counted = data.paymentMethodCounts.find(c => c.paymentMethodName === pm.paymentMethodName);
      const countedAmount = counted?.countedAmount || 0;
      return [pm.paymentMethodName, pm.totalSales, countedAmount, countedAmount - pm.totalSales];
    })
  ];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(pmData), "Formas Pagamento");

  const fileName = `fechamento-caixa-${format(new Date(data.cashRegister.openedAt), "yyyy-MM-dd")}.xlsx`;
  XLSX.writeFile(workbook, fileName);
}

// ============ CANCELLATIONS REPORT EXPORTS ============

interface CancellationRecord {
  cancelled_at: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  reason: string | null;
  order?: { order_number: number; table_number?: string; tab_number?: string } | null;
  created_by_profile?: { full_name: string } | null;
  cancelled_by_profile?: { full_name: string } | null;
}

export function exportCancellationsToPDF(
  cancellations: CancellationRecord[],
  totalValue: number,
  dateRange: string,
  restaurantName?: string
) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;
  const lineHeight = 6;
  const margin = 14;

  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Relatório de Cancelamentos", pageWidth / 2, yPos, { align: "center" });
  yPos += lineHeight * 1.5;

  if (restaurantName) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(restaurantName, pageWidth / 2, yPos, { align: "center" });
    yPos += lineHeight;
  }

  doc.setFontSize(10);
  doc.text(`Período: ${dateRange}`, pageWidth / 2, yPos, { align: "center" });
  yPos += lineHeight * 2;

  // Summary
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text(`Total de Cancelamentos: ${cancellations.length}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Valor Total: ${formatCurrency(totalValue)}`, margin, yPos);
  yPos += lineHeight * 2;

  // Table header
  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("Data/Hora", margin, yPos);
  doc.text("Pedido", 45, yPos);
  doc.text("Produto", 65, yPos);
  doc.text("Qtd", 115, yPos);
  doc.text("Valor", 130, yPos);
  doc.text("Motivo", 155, yPos);
  yPos += lineHeight;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7);
  cancellations.forEach((c) => {
    if (yPos > 275) {
      doc.addPage();
      yPos = 20;
    }
    doc.text(format(new Date(c.cancelled_at), "dd/MM HH:mm"), margin, yPos);
    doc.text(`#${c.order?.order_number || "-"}`, 45, yPos);
    doc.text(c.product_name.substring(0, 25), 65, yPos);
    doc.text(String(c.quantity), 115, yPos);
    doc.text(formatCurrency(c.total_price), 130, yPos);
    doc.text((c.reason || "-").substring(0, 20), 155, yPos);
    yPos += lineHeight;
  });

  // Footer
  doc.setFontSize(8);
  doc.text(
    `Gerado em ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}`,
    pageWidth / 2,
    290,
    { align: "center" }
  );

  doc.save(`cancelamentos-${format(new Date(), "yyyy-MM-dd")}.pdf`);
}

export function exportCancellationsToExcel(
  cancellations: CancellationRecord[],
  totalValue: number,
  dateRange: string
) {
  const workbook = XLSX.utils.book_new();

  const data = cancellations.map(c => ({
    "Data/Hora": format(new Date(c.cancelled_at), "dd/MM/yyyy HH:mm"),
    "Pedido": c.order?.order_number || "-",
    "Mesa": c.order?.table_number || "-",
    "Comanda": c.order?.tab_number || "-",
    "Produto": c.product_name,
    "Quantidade": c.quantity,
    "Valor Unitário": c.unit_price,
    "Valor Total": c.total_price,
    "Motivo": c.reason || "-",
    "Lançado por": c.created_by_profile?.full_name || "-",
    "Cancelado por": c.cancelled_by_profile?.full_name || "-",
  }));

  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(data), "Cancelamentos");

  // Summary sheet
  const summary = [
    ["Relatório de Cancelamentos"],
    [`Período: ${dateRange}`],
    [],
    ["Total de Cancelamentos", cancellations.length],
    ["Valor Total", totalValue],
  ];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summary), "Resumo");

  XLSX.writeFile(workbook, `cancelamentos-${format(new Date(), "yyyy-MM-dd")}.xlsx`);
}

// ============ ENTRADAS REPORT EXPORTS ============

interface EntradaRecord {
  created_at: string;
  command_number: string;
  entry_type_name: string;
  entry_mode: string;
  amount: number;
  status: string;
}

interface EntradasReportData {
  totalEntries: number;
  totalAmount: number;
  byType: Array<{ name: string; count: number; amount: number }>;
}

export function exportEntradasToPDF(
  entries: EntradaRecord[],
  report: EntradasReportData,
  dateRange: string,
  restaurantName?: string
) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;
  const lineHeight = 7;
  const margin = 14;

  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Relatório de Entradas", pageWidth / 2, yPos, { align: "center" });
  yPos += lineHeight * 1.5;

  if (restaurantName) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(restaurantName, pageWidth / 2, yPos, { align: "center" });
    yPos += lineHeight;
  }

  doc.setFontSize(10);
  doc.text(`Período: ${dateRange}`, pageWidth / 2, yPos, { align: "center" });
  yPos += lineHeight * 2;

  // Summary
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Resumo", margin, yPos);
  yPos += lineHeight;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Total de Entradas: ${report.totalEntries}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Valor Total: ${formatCurrency(report.totalAmount)}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Ticket Médio: ${formatCurrency(report.totalEntries > 0 ? report.totalAmount / report.totalEntries : 0)}`, margin, yPos);
  yPos += lineHeight * 2;

  // By Type
  if (report.byType.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Por Tipo", margin, yPos);
    yPos += lineHeight;

    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.text("Tipo", margin, yPos);
    doc.text("Qtd", 100, yPos);
    doc.text("Total", 130, yPos);
    yPos += lineHeight;

    doc.setFont("helvetica", "normal");
    report.byType.forEach((item) => {
      doc.text(item.name, margin, yPos);
      doc.text(String(item.count), 100, yPos);
      doc.text(formatCurrency(item.amount), 130, yPos);
      yPos += lineHeight;
    });
    yPos += lineHeight;
  }

  // Detail table
  if (entries.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Detalhamento", margin, yPos);
    yPos += lineHeight;

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Data/Hora", margin, yPos);
    doc.text("Comanda", 50, yPos);
    doc.text("Tipo", 80, yPos);
    doc.text("Modo", 120, yPos);
    doc.text("Valor", 145, yPos);
    doc.text("Status", 175, yPos);
    yPos += lineHeight;

    doc.setFont("helvetica", "normal");
    entries.forEach((e) => {
      if (yPos > 275) {
        doc.addPage();
        yPos = 20;
      }
      doc.text(format(new Date(e.created_at), "dd/MM HH:mm"), margin, yPos);
      doc.text(e.command_number.padStart(3, "0"), 50, yPos);
      doc.text(e.entry_type_name.substring(0, 20), 80, yPos);
      doc.text(e.entry_mode === "fixed" ? "Fixa" : "Consumação", 120, yPos);
      doc.text(formatCurrency(e.amount), 145, yPos);
      doc.text(e.status === "active" ? "Ativo" : e.status === "closed" ? "Fechado" : "Cancelado", 175, yPos);
      yPos += lineHeight;
    });
  }

  // Footer
  doc.setFontSize(8);
  doc.text(
    `Gerado em ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}`,
    pageWidth / 2,
    290,
    { align: "center" }
  );

  doc.save(`entradas-${format(new Date(), "yyyy-MM-dd")}.pdf`);
}

export function exportEntradasToExcel(
  entries: EntradaRecord[],
  report: EntradasReportData,
  dateRange: string
) {
  const workbook = XLSX.utils.book_new();

  // Summary sheet
  const summaryData = [
    ["Relatório de Entradas"],
    [`Período: ${dateRange}`],
    [],
    ["Total de Entradas", report.totalEntries],
    ["Valor Total", report.totalAmount],
    ["Ticket Médio", report.totalEntries > 0 ? report.totalAmount / report.totalEntries : 0],
  ];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryData), "Resumo");

  // By Type sheet
  if (report.byType.length > 0) {
    const typeData = [
      ["Tipo", "Quantidade", "Total"],
      ...report.byType.map(t => [t.name, t.count, t.amount])
    ];
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(typeData), "Por Tipo");
  }

  // Detail sheet
  const detailData = entries.map(e => ({
    "Data/Hora": format(new Date(e.created_at), "dd/MM/yyyy HH:mm"),
    "Comanda": e.command_number,
    "Tipo": e.entry_type_name,
    "Modo": e.entry_mode === "fixed" ? "Fixa" : "Consumação",
    "Valor": e.amount,
    "Status": e.status === "active" ? "Ativo" : e.status === "closed" ? "Fechado" : "Cancelado",
  }));
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(detailData), "Detalhamento");

  XLSX.writeFile(workbook, `entradas-${format(new Date(), "yyyy-MM-dd")}.xlsx`);
}

// ============ MARGINS REPORT EXPORTS ============

interface ProductMargin {
  name: string;
  categoryName: string | null;
  hasRecipe: boolean;
  cost: number;
  price: number;
  marginValue: number;
  marginPercent: number;
}

export function exportMargensToPDF(
  products: ProductMargin[],
  lowMarginProducts: ProductMargin[],
  lowMarginAlertPercentage: number,
  restaurantName?: string
) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  let yPos = 20;
  const lineHeight = 6;
  const margin = 14;

  // Header
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.text("Relatório de Margens", pageWidth / 2, yPos, { align: "center" });
  yPos += lineHeight * 1.5;

  if (restaurantName) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "normal");
    doc.text(restaurantName, pageWidth / 2, yPos, { align: "center" });
    yPos += lineHeight;
  }

  doc.setFontSize(10);
  doc.text(`Gerado em ${format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR })}`, pageWidth / 2, yPos, { align: "center" });
  yPos += lineHeight * 2;

  // Summary
  const productsWithRecipe = products.filter(p => p.hasRecipe);
  const avgMargin = productsWithRecipe.length > 0
    ? productsWithRecipe.reduce((sum, p) => sum + p.marginPercent, 0) / productsWithRecipe.length
    : 0;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Resumo", margin, yPos);
  yPos += lineHeight;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(`Total de Produtos: ${products.length}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Produtos com Receita: ${productsWithRecipe.length}`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Margem Média: ${avgMargin.toFixed(1)}%`, margin, yPos);
  yPos += lineHeight;
  doc.text(`Produtos com Margem Baixa (< ${lowMarginAlertPercentage}%): ${lowMarginProducts.length}`, margin, yPos);
  yPos += lineHeight * 2;

  // Low margin products
  if (lowMarginProducts.length > 0) {
    doc.setFontSize(14);
    doc.setFont("helvetica", "bold");
    doc.text("Produtos com Margem Baixa", margin, yPos);
    yPos += lineHeight;

    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text("Produto", margin, yPos);
    doc.text("Categoria", 70, yPos);
    doc.text("Custo", 110, yPos);
    doc.text("Preço", 135, yPos);
    doc.text("Margem", 160, yPos);
    yPos += lineHeight;

    doc.setFont("helvetica", "normal");
    lowMarginProducts.forEach((p) => {
      doc.text(p.name.substring(0, 30), margin, yPos);
      doc.text((p.categoryName || "-").substring(0, 20), 70, yPos);
      doc.text(formatCurrency(p.cost), 110, yPos);
      doc.text(formatCurrency(p.price), 135, yPos);
      doc.text(`${p.marginPercent.toFixed(1)}%`, 160, yPos);
      yPos += lineHeight;
    });
    yPos += lineHeight;
  }

  // All products table
  doc.addPage();
  yPos = 20;

  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text("Todos os Produtos", margin, yPos);
  yPos += lineHeight;

  doc.setFontSize(8);
  doc.setFont("helvetica", "bold");
  doc.text("Produto", margin, yPos);
  doc.text("Categoria", 60, yPos);
  doc.text("Receita", 100, yPos);
  doc.text("Custo", 120, yPos);
  doc.text("Preço", 145, yPos);
  doc.text("Margem R$", 165, yPos);
  doc.text("Margem %", 185, yPos);
  yPos += lineHeight;

  doc.setFont("helvetica", "normal");
  products.forEach((p) => {
    if (yPos > 280) {
      doc.addPage();
      yPos = 20;
    }
    doc.text(p.name.substring(0, 25), margin, yPos);
    doc.text((p.categoryName || "-").substring(0, 18), 60, yPos);
    doc.text(p.hasRecipe ? "Sim" : "Não", 100, yPos);
    doc.text(p.hasRecipe ? formatCurrency(p.cost) : "-", 120, yPos);
    doc.text(formatCurrency(p.price), 145, yPos);
    doc.text(p.hasRecipe ? formatCurrency(p.marginValue) : "-", 165, yPos);
    doc.text(p.hasRecipe ? `${p.marginPercent.toFixed(1)}%` : "-", 185, yPos);
    yPos += lineHeight;
  });

  doc.save(`margens-${format(new Date(), "yyyy-MM-dd")}.pdf`);
}

export function exportMargensToExcel(
  products: ProductMargin[],
  lowMarginProducts: ProductMargin[],
  lowMarginAlertPercentage: number
) {
  const workbook = XLSX.utils.book_new();

  // Summary
  const productsWithRecipe = products.filter(p => p.hasRecipe);
  const avgMargin = productsWithRecipe.length > 0
    ? productsWithRecipe.reduce((sum, p) => sum + p.marginPercent, 0) / productsWithRecipe.length
    : 0;

  const summaryData = [
    ["Relatório de Margens"],
    [`Gerado em ${format(new Date(), "dd/MM/yyyy HH:mm")}`],
    [],
    ["Total de Produtos", products.length],
    ["Produtos com Receita", productsWithRecipe.length],
    ["Margem Média", `${avgMargin.toFixed(1)}%`],
    ["Limite de Alerta", `${lowMarginAlertPercentage}%`],
    ["Produtos com Margem Baixa", lowMarginProducts.length],
  ];
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet(summaryData), "Resumo");

  // All products
  const allData = products.map(p => ({
    "Produto": p.name,
    "Categoria": p.categoryName || "-",
    "Tem Receita": p.hasRecipe ? "Sim" : "Não",
    "Custo": p.hasRecipe ? p.cost : null,
    "Preço": p.price,
    "Margem R$": p.hasRecipe ? p.marginValue : null,
    "Margem %": p.hasRecipe ? p.marginPercent : null,
    "Abaixo Limite": p.hasRecipe && p.marginPercent < lowMarginAlertPercentage ? "Sim" : "Não",
  }));
  XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(allData), "Todos Produtos");

  // Low margin products
  if (lowMarginProducts.length > 0) {
    const lowData = lowMarginProducts.map(p => ({
      "Produto": p.name,
      "Categoria": p.categoryName || "-",
      "Custo": p.cost,
      "Preço": p.price,
      "Margem R$": p.marginValue,
      "Margem %": p.marginPercent,
    }));
    XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(lowData), "Margem Baixa");
  }

  XLSX.writeFile(workbook, `margens-${format(new Date(), "yyyy-MM-dd")}.xlsx`);
}
